import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isApiError } from "../api/client";
import { useApi } from "../api/context";
import type { Me } from "../api/types";

/**
 * Who is signed in, from `GET /v1/auth/me` (the HttpOnly session cookie is the credential).
 * - `anonymous`: no or expired session → auth pages;
 * - `setup`: restricted `2fa_setup` session (mandatory 2FA, ADR-0013) → setup page only;
 * - `ready`: full session;
 * - `unreachable`: the API could not be reached (shown with a retry, never as "signed out").
 */
export type AuthState =
  | { readonly status: "loading" }
  | { readonly status: "anonymous" }
  | { readonly status: "unreachable" }
  | { readonly status: "setup"; readonly me: Me }
  | { readonly status: "ready"; readonly me: Me };

interface AuthContextValue {
  readonly state: AuthState;
  refresh(): Promise<AuthState>;
  signOut(): Promise<void>;
}

/** Q4-XCT-02: dispatched by the API client when a call returns 401 (expired session). */
export const SESSION_EXPIRED_EVENT = "wato:session-expired";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const refresh = useCallback(async (): Promise<AuthState> => {
    let next: AuthState;
    try {
      const me = await api.me();
      next = me.session.stage === "full" ? { status: "ready", me } : { status: "setup", me };
    } catch (error) {
      next =
        isApiError(error) && error.status === 0
          ? { status: "unreachable" }
          : { status: "anonymous" };
    }
    setState(next);
    return next;
  }, [api]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setState({ status: "anonymous" });
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // An expired session flips to anonymous at once; `RequireAuth` then redirects to
  // `/login?next=<current path>` without waiting for a full reload.
  useEffect(() => {
    const onExpired = () =>
      setState((prev) => (prev.status === "ready" ? { status: "anonymous" } : prev));
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const value = useMemo(() => ({ state, refresh, signOut }), [state, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Like `useAuth()` but `null` outside `<AuthProvider>` (widgets that also render standalone). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth() must be used inside <AuthProvider>");
  return value;
}
